// 효과 스프라이트 묶음 머티리얼(TSL, vfx-library v1 material.ts를 인스턴스 속성으로 바꾼 것). WebGPU와 WebGL2에서 같은 노드 그래프로 돈다.
// 라이브러리는 스프라이트 한 장마다 uniform을 가진 메시(드로우콜 1회)였다. 혼전에서 동시 효과가 수십 개라
// 텍스처 하나당 InstancedMesh 하나로 묶고, 장마다 다른 값(색·세기·불투명도·획 머리/꼬리·재채색·진행축)은 인스턴스 속성으로 넘긴다.
// - 가산(add): 검은 배경 텍스처를 그대로 더한다. 검은 노이즈는 0.03 빼서 지운다.
// - 알파(alpha): 먼지·혈흔처럼 어두운 색이 있는 텍스처. 밝기에서 알파를 뽑고 색을 되돌린다(un-premultiply).
// - reveal: uv 진행축 p 위로 head(앞머리)와 tail(꼬리)을 움직여 '그려지고 사라지는' 획을 만든다.
// - recolor: 0이면 원색, 1이면 밝기만 남기고 tint로 다시 칠한다(심은 하얗게 유지).
import * as THREE from 'three/webgpu';
import { clamp, dot, float, instancedDynamicBufferAttribute, max, mix, pow, smoothstep, texture, uv, vec3 } from 'three/tsl';
import type { Node } from 'three/webgpu';

export type BlendKind = 'add' | 'alpha';

/** 인스턴스 속성: tint = (r, g, b, 세기), ctrl = (불투명도, head, tail, recolor), axis = (진행축 x, y) */
export interface BatchAttrs {
  tint: THREE.InstancedBufferAttribute;
  ctrl: THREE.InstancedBufferAttribute;
  axis: THREE.InstancedBufferAttribute;
}

export function makeBatchMaterial(initial: THREE.Texture, blend: BlendKind, a: BatchAttrs): { material: THREE.MeshBasicNodeMaterial; map: { value: THREE.Texture } } {
  // 타입 선언이 버퍼 속성 노드를 Node<string>으로 돌려주므로 실제 타입으로 좁힌다(셰이더는 그대로)
  const T = instancedDynamicBufferAttribute(a.tint, 'vec4') as unknown as Node<'vec4'>;
  const C = instancedDynamicBufferAttribute(a.ctrl, 'vec4') as unknown as Node<'vec4'>;
  const A = instancedDynamicBufferAttribute(a.axis, 'vec2') as unknown as Node<'vec2'>;
  const texNode = texture(initial, uv());
  const tint = T.xyz, intensity = T.w;
  const opacity = C.x, head = C.y, tail = C.z, recolor = C.w;

  const rgb = texNode.rgb;
  const p = dot(uv(), A);
  const reveal = float(1).sub(smoothstep(head.sub(0.06), head, p)).mul(smoothstep(tail, tail.add(0.25), p));
  const lum = dot(rgb, vec3(0.299, 0.587, 0.114));

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  material.side = THREE.DoubleSide;
  // 양면 투명 머티리얼은 기본으로 뒷면·앞면 두 번 그린다(드로우콜 2배). 가산·알파 스프라이트는 정렬이 필요 없어 한 번에 그린다.
  material.forceSinglePass = true;
  material.fog = false;

  if (blend === 'add') {
    const base = max(rgb.sub(0.03), vec3(0));
    const recol = tint.mul(lum).mul(1.8).add(vec3(pow(lum, 4)).mul(0.9));
    const col = mix(base.mul(tint), recol, recolor).mul(intensity);
    material.blending = THREE.AdditiveBlending;
    material.colorNode = col;
    material.opacityNode = reveal.mul(opacity);
  } else {
    const m = max(max(rgb.r, rgb.g), rgb.b);
    const al = smoothstep(0.03, 0.2, m);
    const col = clamp(rgb.div(max(al, 0.05)), 0, 1).mul(tint).mul(intensity);
    material.blending = THREE.NormalBlending;
    material.colorNode = col;
    material.opacityNode = al.mul(reveal).mul(opacity);
  }
  return { material, map: texNode as unknown as { value: THREE.Texture } };
}

